import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
  LinearProgress,
  useTheme,
  Fade,
  Zoom,
  Grid,
  useMediaQuery,
  IconButton
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate } from "react-router-dom";
import Navbar from "../../Components/Navbar";

const QUESTIONS = [
  {
    id: "experience",
    title: "How long have you been working?",
    subtitle: "We'll find the best templates for your experience level.",
    options: [
      "No Experience",
      "Less than 3 years",
      "3-5 Years",
      "5-10 Years",
      "10+ Years",
    ],
    icon: "💼",
  },
  {
    id: "student",
    title: "Are you a student?",
    options: ["Yes", "No"],
    icon: "🎓",
  },
  {
    id: "education",
    title: "Select the option that best describes your education level.",
    subtitle:
      "Your education background can help us guide you through relevant sections for your resume.",
    options: [
      "Secondary School",
      "Vocational Certificate or Diploma",
      "Apprenticeship or Internship Training",
      "Associates",
      "Bachelors",
      "Masters",
      "Doctorate or Ph.D.",
    ],
    icon: "📚",
  },
];

function OptionCard({ label, selected, onClick, index }) {
  const theme = useTheme();
  
  return (
    <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
      <Card
        elevation={selected ? 4 : 0}
        sx={{
          borderRadius: 3,
          borderWidth: 2,
          borderStyle: "solid",
          transition: "all 220ms ease",
          borderColor: selected ? "#002B5B" : "#B3D9F7",
          bgcolor: selected ? "#002B5B" : "background.paper",
          color: selected ? "#fff" : "text.primary",
          outline: "none",
          ":hover": {
            transform: selected ? "scale(1.01)" : "translateY(-2px)",
            boxShadow: selected ? 6 : 3,
            borderColor: selected ? "#002B5B" : "#0288D1",
          },
        }}
      >
        <CardActionArea onClick={onClick} sx={{ borderRadius: 3, py: 1 }}>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
              {selected && <CheckIcon fontSize="small" sx={{ color: "#fff" }} />}
              <Typography sx={{ 
                fontWeight: 600, 
                textAlign: "center",
                color: selected ? "#fff" : "#0D4C8B",
              }}>
                {label}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Zoom>
  );
}

export default function ResumeQuestionnaire({ answers, setAnswers }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState("forward");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const total = QUESTIONS.length;
  const current = QUESTIONS[step];

  const progress = useMemo(() => Math.round(((Object.keys(answers).length) / total) * 100), [answers, total]);

  // ✅ ADDED: Save answers to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("questionnaireAnswers", JSON.stringify(answers));
  }, [answers]);

  const handleSelect = (qid, option) => {
    setAnswers((prev) => ({ ...prev, [qid]: option }));
    if (step < total - 1) {
      setDirection("forward");
      setTimeout(() => setStep((s) => Math.min(s + 1, total - 1)), 220);
    }
  };

  const handleBack = () => {
    setDirection("backward");
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleNext = () => {
    if (step < total - 1) {
      setDirection("forward");
      setStep((s) => Math.min(s + 1, total - 1));
    }
  };

  const handleRestart = () => {
    setAnswers({});
    setStep(0);
    setDirection("forward");
  };

  const isComplete = Object.keys(answers).length === total;

  return (
    <>
      {/* ✅ Top Navbar */}
      <Navbar />

      <Box sx={{ 
        width: "100%", 
        minHeight: "100vh", 
        bgcolor: "#EFF7FF",
        display: "flex",
        justifyContent: "center",
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, md: 6 },
      }}>
        <Box sx={{ 
          width: "100%",
          maxWidth: "900px",
        }}>
          <Stack spacing={4}>
            {/* 🔙 Back Button */}
            <Box>
              <IconButton
                onClick={() => navigate('/steps')}
                sx={{
                  ml: -1,
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(0,43,91,0.15)',
                  border: '1.5px solid #B3D9F7',
                  '&:hover': { 
                    backgroundColor: '#EFF7FF',
                    borderColor: '#0288D1',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <ArrowBackIcon sx={{ color: '#002B5B' }} />
              </IconButton>
            </Box>

            {/* Header Section */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: "wrap" }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  color: "white",
                  flexShrink: 0,
                }}>
                  <Typography variant="h6" fontWeight={700}>CV</Typography>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: "#002B5B" }}>
                  Build Your Professional Resume
                </Typography>
              </Stack>
              
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Typography variant="body2" sx={{ color: "#0D4C8B" }}>
                  Answer a few questions to get personalized resume recommendations
                </Typography>
                <Chip 
                  label={`Step ${Math.min(step + 1, total)} of ${total}`} 
                  sx={{
                    background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                    color: "#fff",
                    fontWeight: "bold",
                  }}
                />
              </Stack>
            </Box>

            {/* Progress Bar */}
            <Box>
              <LinearProgress 
                variant="determinate" 
                value={isComplete ? 100 : progress} 
                sx={{ 
                  borderRadius: 99, 
                  height: 10,
                  backgroundColor: "#B3D9F7",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 99,
                    background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                  }
                }} 
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: "#0D4C8B" }}>
                  {isComplete ? "Completed! 🎉" : "Progress..."}
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ color: "#002B5B" }}>
                  {isComplete ? "100%" : `${progress}%`}
                </Typography>
              </Stack>
            </Box>

            {/* Main Content */}
            <Fade in={true} key={step}>
              <Box>
                {!isComplete ? (
                  <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h4">{current.icon}</Typography>
                      <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: "#002B5B" }}>
                          {current.title}
                        </Typography>
                        {current.subtitle && (
                          <Typography variant="body2" sx={{ color: "#0D4C8B" }}>
                            {current.subtitle}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    <Grid container spacing={2}>
                      {current.options.map((opt, index) => (
                        <Grid item xs={12} md={6} key={opt}>
                          <OptionCard
                            label={opt}
                            selected={answers[current.id] === opt}
                            onClick={() => handleSelect(current.id, opt)}
                            index={index}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : (
                  <Box>
                    <Stack spacing={1} sx={{ mb: 3 }}>
                      <Typography variant="h4" textAlign="center" fontWeight={700} sx={{ color: "#002B5B" }}>
                        You're All Set! 🎉
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#0D4C8B", textAlign: "center" }}>
                        Here's a summary of your answers. You can continue to create your resume or go back to edit any answer.
                      </Typography>
                    </Stack>

                    <Stack spacing={2}>
                      {QUESTIONS.map((q, idx) => (
                        <Card 
                          key={q.id} 
                          variant="outlined" 
                          sx={{ 
                            borderRadius: 3,
                            borderLeft: `4px solid #002B5B`,
                            borderColor: "#B3D9F7",
                            boxShadow: "0 2px 8px rgba(0,43,91,0.06)",
                            "&:hover": {
                              boxShadow: "0 4px 16px rgba(0,43,91,0.12)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          <CardContent>
                            <Stack spacing={1}>
                              <Typography variant="subtitle2" sx={{ color: "#0D4C8B" }}>
                                {q.icon} {idx + 1}. {q.title}
                              </Typography>
                              <Chip
                                sx={{ 
                                  background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                                  color: "#fff",
                                  fontWeight: 600,
                                  width: "fit-content" 
                                }}
                                label={answers[q.id]}
                              />
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 4 }}>
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate("/resume")}
                        sx={{
                          background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                          color: "#fff",
                          fontWeight: "bold",
                          py: 1.5,
                          borderRadius: "12px",
                          textTransform: "none",
                          fontSize: "1rem",
                          boxShadow: "0 4px 16px rgba(0,43,91,0.3)",
                          "&:hover": {
                            background: "linear-gradient(135deg, #001a36 0%, #026aa1 100%)",
                            boxShadow: "0 6px 24px rgba(2,136,209,0.4)",
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Create My Resume
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="large" 
                        fullWidth
                        startIcon={<RestartAltIcon />}
                        onClick={handleRestart}
                        sx={{
                          borderColor: "#002B5B",
                          color: "#002B5B",
                          fontWeight: "bold",
                          borderRadius: "12px",
                          textTransform: "none",
                          fontSize: "1rem",
                          "&:hover": {
                            borderColor: "#0288D1",
                            backgroundColor: "rgba(2, 136, 209, 0.04)",
                            color: "#0288D1",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        Start Over
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Fade>

            {/* Navigation */}
            {!isComplete && (
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
                
                {answers[current.id] && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      background: "linear-gradient(135deg, #002B5B 0%, #0288D1 100%)",
                      color: "#fff",
                      fontWeight: "bold",
                      borderRadius: "12px",
                      textTransform: "none",
                      px: { xs: 3, sm: 4 },
                      py: { xs: 1.2, sm: 1.5 },
                      boxShadow: "0 4px 16px rgba(0,43,91,0.25)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #001a36 0%, #026aa1 100%)",
                        boxShadow: "0 6px 24px rgba(2,136,209,0.35)",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Next
                  </Button>
                )}
                
                <Typography variant="caption" sx={{ color: "#0D4C8B", display: { xs: 'none', sm: 'block' } }}>
                  Tip: Click an option to auto-advance
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    </>
  );
}